import React, {useEffect, useState} from 'react';
import clsx from 'clsx';
import {i18n} from '@netx/core/i18n';
import {Skeleton} from '@mui/material';
import {styled} from './styled';
import {composeClasses} from './utils/composeClasses';
import {useTheme} from './hooks';
import type {Theme} from './types';
import type {WithClasses} from './types';
import useDispatcher from '@netx/core-components/hooks/useDispatcher';
import useImageLoader from '@netx/core-components/hooks/useImageLoader';
import {URL} from '@netx/core-next/url';

import {
	logoClasses,
	getLogoUtilityClass,
	LogoClasses,
	LOGO_CLASS_NAME,
} from './logoClasses';
import {useConstCallback} from '../hooks/useEvent';

type LogoOwnerState = WithClasses<LogoClasses, {}>;

const useUtilityClasses = (ownerState: LogoOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
		image: ['image'],
		skeleton: ['skeleton'],
	};
	return composeClasses(slots, getLogoUtilityClass, classes);
};

const LogoRootDiv = styled('div', {
	name: LOGO_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	flex: '0 0 auto',
	maxHeight: 42,
}));

const LogoRootAnchor = styled('a', {
	name: LOGO_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	flex: '0 0 auto',
	maxHeight: 42,
}));

const LogoImage = styled('img', {
	name: LOGO_CLASS_NAME,
	slot: 'Image',
})(({theme}) => ({
	maxHeight: 42,
	maxWidth: '100%',
	width: 'auto',
	height: 'auto',
	[theme.breakpoints.down('sm')]: {
		maxHeight: 32,
	},
}));

const LogoSkeleton = styled(Skeleton, {
	name: LOGO_CLASS_NAME,
	slot: 'Skeleton',
})(({theme}) => ({
	borderRadius: 4,
	width: 116,
	height: 42,
	[theme.breakpoints.down('sm')]: {
		height: 32,
		width: 88,
	},
}));

let lastThemeMode: 'light' | 'dark' = 'light';

const getLogoUrl = (theme: Theme, force = false) => {
	let url =
		URL.themeLogo ||
		(theme.palette.mode === 'dark' ? URL.logoDark : URL.logoLight);
	if (force || lastThemeMode !== theme.palette.mode) {
		url = `${url}?cb=${Date.now()}`;
	}
	lastThemeMode = theme.palette.mode;
	return url;
};

export interface LogoProps extends WithClasses<LogoClasses> {
	disableXHR?: boolean;
	alt?: string;
	href?: string;
	title?: string;
}

export const LogoPrimitive = ({
	classes: _classes,
	className,
	alt,
	disableXHR,
	href,
	title,
}: LogoProps) => {
	const theme = useTheme();
	const imageLoader = useImageLoader();
	const [src, setSrc] = useState(getLogoUrl(theme));

	const classes = useUtilityClasses({
		classes: _classes,
		className,
	});

	useEffect(() => {
		if (disableXHR) return;
		imageLoader.load(src);
	}, [src, disableXHR]);

	const handleLogoUpdate = useConstCallback((force?: boolean) => {
		setSrc(getLogoUrl(theme, force));
	});

	useEffect(() => {
		handleLogoUpdate();
	}, [theme.palette.mode]);

	useDispatcher('app:system:branding:updated', () => handleLogoUpdate(true));
	useDispatcher('app:system:branding:reset:confirmed', () =>
		handleLogoUpdate(true),
	);

	const imgSrc = disableXHR ? src : imageLoader.src;

	const RootComponent = href ? LogoRootAnchor : LogoRootDiv;

	return (
		<RootComponent
			className={clsx(classes.root, className)}
			href={href}
			role={href ? 'link' : undefined}
			title={title}
		>
			{imageLoader.src || disableXHR ? (
				<LogoImage
					className={classes.image}
					src={imgSrc}
					alt={alt ?? i18n.t('i18n.logo')}
				/>
			) : (
				<LogoSkeleton className={classes.skeleton} variant="rectangular" />
			)}
		</RootComponent>
	);
};

export const Logo = LogoPrimitive;

export default Logo;
